import {
  getSupplierMatchComponent,
  hasSingleOption,
  isSupplierMatch,
} from './supplierMatches';
import {
  getComponentIdsRequiringOtherSelection,
  getRequiredOptionIds,
} from '../Components/__selectors__/component.selectors';
import { isLoaderComponent } from '../Components/__helpers__/component.helpers';
import { ComponentTypes } from '../Data/Components';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../Data/Country';
import Subcategories from '../Data/Subcategories';

const getComponentFromOptionId = (optionId, components) => {
  return Object.values(components).find(
    (component) =>
      component.input?.options &&
      component.input.options.some(
        (option) => String(option.id) === String(optionId)
      )
  );
};

const isAConditionalChildComponent = (component) =>
  !!(
    component?.component?.conditional &&
    Object.values(component.component.conditional).some(
      (condition) => condition.length
    )
  );

const isPotentiallyHiddenSupplierMatch = (component) =>
  component?.component?.hideOnSingleMatch;

const isComponentOnLoaderPage = (component, components) =>
  Object.values(components)
    .filter(isLoaderComponent)
    .some(
      (loaderComponent) =>
        loaderComponent?.component?.page === component?.component?.page
    );

export const isComponentPotentiallySkippable = (component, components) =>
  isAConditionalChildComponent(component) ||
  isPotentiallyHiddenSupplierMatch(component) ||
  isComponentOnLoaderPage(component, components);

/**
 * Some components will only show to a user if certain other options have been selected.
 * They can be set to show if ALL of the options are selected or if SOME of them are.
 * This is controlled in Capture using the AND conditional.
 * i.e. a component which is conditional on options ['1', '2'] and is using the AND conditional
 * will only show if both options 1 and 2 have been selected. Whereas if it is not using the AND
 * conditional then it will show if 1 OR 2 are selected.
 */
const hasAndConditional = (component) =>
  isAConditionalChildComponent(component)
    ? !!component.component?.hasAndConditional
    : false;

const isOptionSelected = (optionId, components) => {
  const component = getComponentFromOptionId(optionId, components);
  return (
    typeof component?.input?.selectedOptions === 'object' &&
    component.input.selectedOptions.some(
      (selectedOption) => String(selectedOption) === String(optionId)
    ) &&
    isConditionallyActive(components, component)
  );
};

const areRequiredOptionsSelected = (components, component) => {
  const stringRequiredOptionIds = getRequiredOptionIds(component).map((id) =>
    String(id)
  );

  return hasAndConditional(component)
    ? stringRequiredOptionIds.every((optionId) =>
        isOptionSelected(optionId, components)
      )
    : stringRequiredOptionIds.some((optionId) =>
        isOptionSelected(optionId, components)
      );
};

export const isOtherSelected = (component) =>
  Boolean(component?.input?.selectedOptions?.includes('other-option'));

const isActiveWithOtherSelected = (componentId, components) => {
  const parentComponent = components[`component_${componentId}`];
  return (
    isOtherSelected(parentComponent) &&
    isConditionallyActive(components, parentComponent)
  );
};

/**
 * Checks if all required other options have been selected.
 * All other options use the 'other-option' value so in order to distinguish if a specific
 * other-option has been selected we must check the component directly.
 */
const areRequiredOtherOptionsSelected = (components, component) => {
  const componentIdsRequiringOtherSelection =
    getComponentIdsRequiringOtherSelection(component);

  return hasAndConditional(component)
    ? componentIdsRequiringOtherSelection.every((componentId) =>
        isActiveWithOtherSelected(componentId, components)
      )
    : componentIdsRequiringOtherSelection.some((componentId) =>
        isActiveWithOtherSelected(componentId, components)
      );
};

const isActiveConditional = (components, component) => {
  return hasAndConditional(component)
    ? areRequiredOtherOptionsSelected(components, component) &&
        areRequiredOptionsSelected(components, component)
    : areRequiredOtherOptionsSelected(components, component) ||
        areRequiredOptionsSelected(components, component);
};

/**
 * There are two versions of hidden component those that have the type of hidden, and those that have been set hiddenInChameleon in capture
 *
 * There also exists the concept of conditionally shown components, i.e. a component which will only be shown to the user if a/many options for a prior question had been selected by the user.
 */
export const isIntentionallyHidden = (component) =>
  component?.component?.type === 'hidden' ||
  component?.input?.hiddenInChameleon;

/**
 * Some components will only show to a user if certain other options have been selected.
 * There are currently two types of conditional components:
 * 1. Components which are conditional on specific options having been selected
 * 2. Components which are conditional on previous components having had the 'other' option selected
 * This function will return true if the component is not conditional or if it is conditional
 * and the conditions are met.
 */
export const isConditionallyActive = (components, component) => {
  if (!isAConditionalChildComponent(component)) {
    return true;
  }

  return isActiveConditional(components, component);
};

/**
 * Determines if a component on a loader page should be hidden based on supplier match visibility.
 *
 * When a page containing a loader component immediately precedes a supplier match page,
 * all components on that loader page are considered dependent on the supplier match component.
 * If the supplier match component would be hidden for any reason (e.g., conditional logic,
 * single match settings), then components on the loader page should also be hidden to
 * maintain a consistent user experience.
 */
const isLoaderPageHiddenDueToSupplierMatchesBeingHidden = ({
  component,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  const componentPage = component?.component?.page;

  if (!componentPage) {
    return false;
  }
  const loaderPages = Object.values(components)
    .filter(isLoaderComponent)
    .map((loaderComponent) => loaderComponent?.component?.page);

  const currentPageIsLoaderPage = loaderPages.includes(componentPage);

  if (!currentPageIsLoaderPage) {
    return false;
  }

  // We only support one supplier match component at the moment so only need to check for the first one
  const firstSupplierMatchComponent = getSupplierMatchComponent({ components });
  if (!firstSupplierMatchComponent) {
    return false;
  }
  const supplierMatchPage = firstSupplierMatchComponent.component.page;

  // Automatically hide any page containing a loader which immediately precedes a supplier match page
  const loaderPrecedesSupplierMatch =
    supplierMatchPage && componentPage + 1 === supplierMatchPage;

  return (
    loaderPrecedesSupplierMatch &&
    !shouldShowComponent(
      components,
      firstSupplierMatchComponent,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    )
  );
};

export const isHiddenDueToNoDynamicNewsletter = (
  component,
  dynamicNewsletterName
) => {
  return (
    component?.component?.type === ComponentTypes.NEWSLETTER_SIGNUP &&
    component?.component?.conditional?.onDynamicNewsletter &&
    dynamicNewsletterName === null
  );
};

const isHiddenDueToPostcodeMatching = (component) =>
  component?.component?.conditional?.requiresPostcodeToNotMatch?.isMatching ===
  true;

/**
 * This condition is set to false for premium channels where we can skip the supplier match component.
 */
export const isHiddenDueToSupplierMatchSelectionDisabled = (component) =>
  component?.component?.conditional?.supplierMatchSelectionEnabled === false;

export const shouldShowComponent = (
  components,
  component,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId
) => {
  return (
    !isHiddenAsPartOfMoveHubSupplierMatchLogic({
      component,
      components,
      phoneNumberCountryCode,
      subcategoryId,
    }) &&
    !isLoaderPageHiddenDueToSupplierMatchesBeingHidden({
      component,
      components,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId,
    }) &&
    !isHiddenDueToSingleSupplierMatch({ component }) &&
    !isHiddenDueToNoDynamicNewsletter(component, dynamicNewsletterName) &&
    !isHiddenDueToPostcodeMatching(component) &&
    !isHiddenDueToSupplierMatchSelectionDisabled(component) &&
    isConditionallyActive(components, component)
  );
};

/*
 * In the subcategory "Removals Autoqualified" we don't show the supplier match
 * component unless the phone number country code is US or the user has selected
 * USA as their current country.
 */
export const isHiddenAsPartOfMoveHubSupplierMatchLogic = ({
  component,
  components,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  if (component?.component?.type !== ComponentTypes.SUPPLIER_MATCH) {
    return false;
  }
  if (subcategoryId !== Subcategories.REMOVALS_AUTOQUALIFIED.id) {
    return false;
  }
  if (phoneNumberCountryCode === 'US') {
    return false;
  }

  const selectedCountryIsUSA = Object.values(components).find(
    (componentFromList) => {
      return (
        componentFromList.component.mappedQuestionId ===
          SELECT_COUNTRY_MAPPED_QUESTION_ID &&
        componentFromList.input?.value?.trim() === 'USA'
      );
    }
  );

  if (selectedCountryIsUSA) {
    return false;
  }

  return true;
};

const isSupplierMatchWithSingleOption = (component) =>
  isSupplierMatch(component) && hasSingleOption(component);

export const isHiddenDueToSingleSupplierMatch = ({ component }) =>
  isSupplierMatchWithSingleOption(component) &&
  component?.component?.hideOnSingleMatch;

export const isFormShowingSupplierCardsConditionallyOnZipCode = (formId) =>
  JSON.parse(
    process.env.NEXT_PUBLIC_CONDITIONAL_SUPPLIER_CARD_FORM_IDS
  ).includes(formId);
